import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PaperPlaneTilt, CheckCircle, XCircle, Copy, ArrowRight, Lightning, ChatCircle,
  WarningCircle, ShieldCheck, Trash, Plus, ClockCounterClockwise, ArrowClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Telegram() {
  const [config, setConfig] = useState({ connected: false, chat_id: "", bot_token_set: false, webhook_secret_set: false, allowed_chats_count: 0 });
  const [form, setForm] = useState({ bot_token: "", chat_id: "", webhook_secret: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [workspaceId, setWorkspaceId] = useState("");
  const [chatOptions, setChatOptions] = useState([]);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [step, setStep] = useState(1);

  // Whitelist state
  const [allowed, setAllowed] = useState([]);
  const [ownerChatId, setOwnerChatId] = useState("");
  const [newAllowed, setNewAllowed] = useState({ chat_id: "", label: "" });

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const loadConfig = async () => {
    const r = await api.get("/telegram/config");
    setConfig(r.data);
    setForm((f) => ({ ...f, chat_id: r.data.chat_id || "" }));
    if (r.data.bot_token_set) setStep(2);
    if (r.data.connected) setStep(4);
  };

  const loadAllowed = async () => {
    try {
      const r = await api.get("/telegram/allowed-chats");
      setAllowed(r.data.allowed || []);
      setOwnerChatId(r.data.owner_chat_id || "");
    } catch { /* ignore */ }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const r = await api.get("/telegram/logs?limit=50");
      setLogs(r.data || []);
    } catch { setLogs([]); }
    finally { setLogsLoading(false); }
  };

  useEffect(() => {
    loadConfig();
    loadAllowed();
    loadLogs();
    api.get("/auth/me").then((r) => setWorkspaceId(r.data.workspace?.id || ""));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/telegram/config", form);
      toast.success("Konfigurasi Telegram tersimpan");
      await loadConfig();
      await loadAllowed();
      if (form.bot_token && !form.chat_id) setStep(2);
      else if (form.chat_id) setStep(3);
    } catch { toast.error("Gagal simpan"); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/telegram/test");
      if (r.data.ok) toast.success("Test message terkirim! Cek Telegram Anda.");
      else toast.error("Gagal: " + (r.data.description || r.data.error || "Unknown"));
    } catch { toast.error("Gagal test"); }
    finally { setTesting(false); }
  };

  const sendReport = async () => {
    try {
      const r = await api.post("/telegram/send-report");
      if (r.data.ok) toast.success("Laporan L/R terkirim ke Telegram");
      else toast.error("Gagal kirim laporan");
    } catch { toast.error("Gagal kirim"); }
  };

  const autoDetectChatId = async () => {
    try {
      const r = await api.get("/telegram/get-chat-id");
      const list = r.data.chats || [];
      if (list.length === 0) {
        toast.error("Belum ada chat terdeteksi. Kirim '/start' ke bot Anda dari Telegram dulu, lalu klik lagi.");
        return;
      }
      setChatOptions(list);
      toast.success(`Ditemukan ${list.length} chat`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal deteksi"); }
  };

  const setupWebhook = async () => {
    try {
      await api.post("/telegram/setup-webhook", { public_base_url: process.env.REACT_APP_BACKEND_URL });
      toast.success("Webhook aktif! Bot siap terima perintah 2 arah.");
      await checkWebhookInfo();
      setStep(4);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal daftar webhook"); }
  };

  const checkWebhookInfo = async () => {
    try { const r = await api.get("/telegram/webhook-info"); setWebhookInfo(r.data.result || r.data); }
    catch { setWebhookInfo(null); }
  };

  const addAllowedChat = async () => {
    if (!newAllowed.chat_id.trim()) { toast.error("Chat ID wajib diisi"); return; }
    try {
      await api.post("/telegram/allowed-chats", newAllowed);
      toast.success(`Chat ${newAllowed.chat_id} diotorisasi`);
      setNewAllowed({ chat_id: "", label: "" });
      await loadAllowed();
      await loadConfig();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal tambah"); }
  };

  const removeAllowedChat = async (chatId) => {
    if (!window.confirm(`Hapus chat ${chatId} dari daftar terotorisasi?`)) return;
    try {
      await api.delete(`/telegram/allowed-chats/${encodeURIComponent(chatId)}`);
      toast.success("Chat dihapus");
      await loadAllowed();
      await loadConfig();
    } catch { toast.error("Gagal hapus"); }
  };

  const clearLogs = async () => {
    if (!window.confirm("Hapus semua riwayat log Telegram?")) return;
    try {
      await api.delete("/telegram/logs");
      toast.success("Riwayat dihapus");
      await loadLogs();
    } catch { toast.error("Gagal hapus"); }
  };

  const webhookUrl = workspaceId ? `${process.env.REACT_APP_BACKEND_URL}/api/telegram/webhook/${workspaceId}` : "";

  const StepBadge = ({ n, active, done }) => (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-emerald-600 text-white" : active ? "bg-amber-600 text-white" : "bg-slate-200 text-slate-500"}`}>
      {done ? <CheckCircle size={14} weight="fill" /> : n}
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl" data-testid="telegram-page">
      <div>
        <h1 className="font-serif-display text-4xl font-medium text-slate-900">Telegram Bot — Komunikasi 2 Arah</h1>
        <p className="text-sm text-slate-500 mt-1">Terhubung Telegram untuk mencatat transaksi + terima laporan otomatis. Whitelist chat, /faktur cepat, foto struk, audit trail.</p>
      </div>

      {/* Status card */}
      <div className="mt-6 bg-white border border-slate-200 rounded-md p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {config.connected ? <CheckCircle size={28} weight="fill" className="text-emerald-600" /> : <XCircle size={28} weight="fill" className="text-slate-400" />}
          <div>
            <div className="text-sm font-medium">{config.connected ? "Bot Aktif" : "Bot Belum Aktif"}</div>
            <div className="text-xs text-slate-500">
              {config.connected ? `Owner Chat ID: ${config.chat_id}` : "Ikuti panduan di bawah untuk aktifkan"}
              {config.allowed_chats_count > 0 && <span className="ml-2 text-emerald-700">· {config.allowed_chats_count} chat terotorisasi</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={test} disabled={!config.bot_token_set || testing} variant="outline" size="sm" data-testid="test-telegram-btn">{testing ? "..." : "Test Kirim"}</Button>
          <Button onClick={sendReport} disabled={!config.connected} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="send-report-btn"><PaperPlaneTilt size={13} className="mr-1" />Kirim Laporan L/R</Button>
        </div>
      </div>

      {/* Panduan Setup */}
      <div className="mt-6 bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-md p-6">
        <h2 className="font-serif-display text-2xl text-slate-900 mb-4">Panduan Setup untuk Pemula 🌱</h2>

        {/* STEP 1 */}
        <div className="border-t border-amber-200 pt-4 mt-2">
          <div className="flex items-center gap-3 mb-3">
            <StepBadge n={1} active={step === 1} done={config.bot_token_set} />
            <h3 className="font-medium text-slate-900">Buat Bot Baru di Telegram</h3>
          </div>
          <div className="pl-10 text-sm text-slate-700 space-y-2">
            <p>a. Buka Telegram di HP/desktop Anda.</p>
            <p>b. Cari kontak: <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-amber-700 font-semibold underline">@BotFather</a> (ada centang biru resmi).</p>
            <p>c. Kirim perintah: <code className="bg-slate-900 text-amber-300 px-1.5 py-0.5 rounded font-mono-plex text-xs">/newbot</code></p>
            <p>d. Ikuti instruksi: masukkan nama bot lalu username (harus diakhiri <code>bot</code>).</p>
            <p>e. BotFather akan kirim <b>Bot Token</b> panjang seperti: <code className="bg-slate-100 px-1 rounded">1234567890:AAH-abc123XYZ...</code></p>
            <div className="bg-amber-100 border border-amber-300 rounded p-2 text-xs mt-2"><WarningCircle size={14} className="inline mr-1" /><b>Simpan token ini rahasia.</b></div>
            <div className="mt-3">
              <Label>Paste Bot Token di sini:</Label>
              <Input type="password" placeholder="1234567890:AA..." value={form.bot_token} onChange={(e) => setForm({ ...form, bot_token: e.target.value })} data-testid="tg-token-input" />
              <Button onClick={save} disabled={!form.bot_token || saving} className="mt-2 bg-amber-600 hover:bg-amber-700 text-white" size="sm" data-testid="tg-save-token-btn">{saving ? "Menyimpan..." : "Simpan Token & Lanjut"} <ArrowRight size={12} className="ml-1" /></Button>
            </div>
          </div>
        </div>

        {/* STEP 2 */}
        <div className="border-t border-amber-200 pt-4 mt-4">
          <div className="flex items-center gap-3 mb-3">
            <StepBadge n={2} active={step === 2} done={!!config.chat_id} />
            <h3 className="font-medium text-slate-900">Dapatkan Chat ID Owner (Utama)</h3>
          </div>
          <div className="pl-10 text-sm text-slate-700 space-y-2">
            <p>a. Di Telegram, buka bot yang baru Anda buat dan kirim <code className="bg-slate-900 text-amber-300 px-1.5 py-0.5 rounded text-xs">/start</code></p>
            <p>b. Klik tombol di bawah — sistem otomatis deteksi chat Anda:</p>
            <Button onClick={autoDetectChatId} disabled={!config.bot_token_set} className="mt-1 bg-slate-900 hover:bg-slate-800 text-white" size="sm" data-testid="tg-detect-chat-btn"><Lightning size={13} className="mr-1" />Deteksi Chat Otomatis</Button>
            {chatOptions.length > 0 && (
              <div className="mt-3 space-y-1">
                <Label>Pilih chat owner:</Label>
                {chatOptions.map((c) => (
                  <button key={c.id} onClick={() => { setForm({ ...form, chat_id: c.id }); toast.success(`Chat ID ${c.id} dipilih`); }} className={`w-full text-left px-3 py-2 border rounded text-xs hover:bg-amber-50 ${form.chat_id === c.id ? "border-amber-500 bg-amber-50" : "border-slate-200"}`} data-testid={`tg-chat-opt-${c.id}`}>
                    <span className="font-mono-plex text-amber-700">{c.id}</span> · <b>{c.title}</b> <span className="text-slate-400">({c.type})</span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2">
              <Label>Atau paste Chat ID manual:</Label>
              <Input placeholder="123456789 atau -100xxx untuk grup" value={form.chat_id} onChange={(e) => setForm({ ...form, chat_id: e.target.value })} data-testid="tg-chatid-input" />
            </div>
            <div className="mt-2">
              <Label>Webhook Secret (opsional, keamanan tambahan):</Label>
              <Input type="password" placeholder="random-string-aman-anda-sendiri" value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} data-testid="tg-secret-input" />
            </div>
            <Button onClick={save} disabled={saving || !form.chat_id} className="mt-2 bg-amber-600 hover:bg-amber-700 text-white" size="sm" data-testid="tg-save-full-btn">{saving ? "..." : "Simpan Chat ID & Lanjut"} <ArrowRight size={12} className="ml-1" /></Button>
          </div>
        </div>

        {/* STEP 3 */}
        <div className="border-t border-amber-200 pt-4 mt-4">
          <div className="flex items-center gap-3 mb-3">
            <StepBadge n={3} active={step === 3} done={step === 4} />
            <h3 className="font-medium text-slate-900">Aktifkan Komunikasi 2 Arah (Webhook)</h3>
          </div>
          <div className="pl-10 text-sm text-slate-700 space-y-2">
            <p>Klik tombol di bawah — sistem otomatis daftar webhook + subscribe ke event <code>callback_query</code> untuk tombol konfirmasi ✅/❌.</p>
            <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
              <div className="text-slate-500 mb-1">URL webhook yang akan didaftarkan:</div>
              <div className="flex items-center gap-2 font-mono-plex text-[10px] break-all">
                <span className="flex-1">{webhookUrl || "..."}</span>
                <button onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Disalin"); }} className="text-slate-500 hover:text-slate-900" data-testid="tg-copy-webhook"><Copy size={12} /></button>
              </div>
            </div>
            <Button onClick={setupWebhook} disabled={!config.connected} className="mt-1 bg-emerald-700 hover:bg-emerald-800 text-white" size="sm" data-testid="tg-setup-webhook-btn"><Lightning size={13} className="mr-1" weight="fill" />Aktifkan Webhook Sekarang</Button>
            <Button onClick={checkWebhookInfo} variant="outline" size="sm" className="ml-2" data-testid="tg-webhook-info-btn">Cek Status</Button>
            {webhookInfo && (
              <div className="mt-2 bg-slate-900 text-slate-100 rounded p-3 font-mono-plex text-[11px] overflow-x-auto">
                <div>URL: <span className={webhookInfo.url ? "text-emerald-400" : "text-rose-400"}>{webhookInfo.url || "(kosong)"}</span></div>
                <div>Pending updates: {webhookInfo.pending_update_count ?? 0}</div>
                {webhookInfo.last_error_message && <div className="text-rose-400">Last error: {webhookInfo.last_error_message}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* WHITELIST — Chat Terotorisasi */}
      <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="tg-whitelist-section">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={22} weight="fill" className="text-emerald-700" />
          <h2 className="font-serif-display text-2xl text-slate-900">Chat Terotorisasi (Whitelist)</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Hanya chat/grup dari daftar ini yang bisa memberi perintah ke bot. Chat lain akan ditolak + Anda mendapat notifikasi.
        </p>

        {ownerChatId && (
          <div className="mb-3 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded p-3">
            <div className="text-xs">
              <div className="font-medium text-emerald-900">👑 Owner Chat (utama)</div>
              <div className="font-mono-plex text-emerald-800">{ownerChatId}</div>
            </div>
            <span className="text-xs text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">otomatis diizinkan</span>
          </div>
        )}

        <div className="space-y-2 mb-4">
          {allowed.length === 0 && <div className="text-xs text-slate-400 italic">Belum ada chat tambahan yang diotorisasi.</div>}
          {allowed.map((c) => (
            <div key={c.chat_id} className="flex items-center justify-between border border-slate-200 rounded p-3" data-testid={`tg-allowed-${c.chat_id}`}>
              <div className="text-xs">
                <div className="font-medium">{c.label || "Chat"}</div>
                <div className="font-mono-plex text-slate-500">{c.chat_id} <span className="text-slate-400">· ditambahkan {c.added_at?.slice(0,10)}</span></div>
              </div>
              <button onClick={() => removeAllowedChat(c.chat_id)} className="text-rose-600 hover:bg-rose-50 p-2 rounded" data-testid={`tg-remove-allowed-${c.chat_id}`}>
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
          <div>
            <Label className="text-xs">Chat ID</Label>
            <Input placeholder="123456789 atau -100xxx" value={newAllowed.chat_id} onChange={(e) => setNewAllowed({ ...newAllowed, chat_id: e.target.value })} data-testid="tg-new-chat-id" />
          </div>
          <div>
            <Label className="text-xs">Label (opsional)</Label>
            <Input placeholder="Grup Keuangan / Handphone Direktur" value={newAllowed.label} onChange={(e) => setNewAllowed({ ...newAllowed, label: e.target.value })} data-testid="tg-new-chat-label" />
          </div>
          <Button onClick={addAllowedChat} className="bg-slate-900 hover:bg-slate-800 text-white" size="sm" data-testid="tg-add-allowed-btn">
            <Plus size={14} className="mr-1" /> Tambah ke Whitelist
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          💡 Tips: setelah menambah bot ke grup, kirim pesan apa saja lalu klik "Deteksi Chat Otomatis" di Step 2 untuk melihat Chat ID grup, lalu paste di sini.
        </p>
      </div>

      {/* AUDIT LOGS */}
      <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="tg-logs-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={22} weight="fill" className="text-slate-700" />
            <h2 className="font-serif-display text-2xl text-slate-900">Riwayat Interaksi Bot</h2>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadLogs} variant="outline" size="sm" data-testid="tg-refresh-logs"><ArrowClockwise size={13} className="mr-1" /> Refresh</Button>
            <Button onClick={clearLogs} variant="outline" size="sm" className="text-rose-600 hover:bg-rose-50" data-testid="tg-clear-logs"><Trash size={13} className="mr-1" /> Hapus Semua</Button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">50 interaksi terakhir. Berguna untuk audit trail siapa mengirim perintah apa dan kapan.</p>

        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-2">Waktu</th>
                <th className="text-left p-2">Chat</th>
                <th className="text-left p-2">Arah</th>
                <th className="text-left p-2">Jenis</th>
                <th className="text-left p-2">Pesan</th>
              </tr>
            </thead>
            <tbody>
              {logsLoading && <tr><td colSpan={5} className="p-3 text-center text-slate-400">Memuat...</td></tr>}
              {!logsLoading && logs.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-slate-400 italic">Belum ada interaksi tercatat.</td></tr>}
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tg-log-${l.id}`}>
                  <td className="p-2 whitespace-nowrap font-mono-plex text-[10px] text-slate-500">{(l.created_at || "").replace("T", " ").slice(0, 19)}</td>
                  <td className="p-2">
                    <div className="font-medium">{l.chat_title || "-"}</div>
                    <div className="font-mono-plex text-[10px] text-slate-400">{l.chat_id}</div>
                  </td>
                  <td className="p-2">
                    {l.direction === "in" ?
                      <span className="text-xs bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded">↓ In</span> :
                      <span className="text-xs bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">↑ Out</span>}
                  </td>
                  <td className="p-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      l.kind === "denied" ? "bg-rose-100 text-rose-800" :
                      l.kind === "photo" ? "bg-violet-100 text-violet-800" :
                      l.kind === "callback" ? "bg-amber-100 text-amber-800" :
                      l.kind === "system" ? "bg-slate-100 text-slate-700" :
                      "bg-slate-100 text-slate-700"
                    }`}>{l.kind}</span>
                  </td>
                  <td className="p-2 max-w-md truncate" title={l.text}>{l.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Perintah */}
      <div className="bg-slate-900 text-white rounded-md p-6 mt-6">
        <h3 className="font-serif-display text-2xl font-medium text-white flex items-center gap-2"><ChatCircle size={22} weight="fill" className="text-amber-400" />Daftar Perintah Bot</h3>
        <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          {[
            { cmd: "/start", desc: "Sambutan + quick start" },
            { cmd: "/bantuan", desc: "Daftar lengkap perintah" },
            { cmd: "/laporan", desc: "Ringkasan Laba/Rugi" },
            { cmd: "/saldo", desc: "Saldo kas & bank" },
            { cmd: "/piutang", desc: "Total piutang outstanding" },
            { cmd: "/hutang", desc: "Total hutang outstanding" },
            { cmd: "/tagihan", desc: "5 faktur terbaru" },
            { cmd: "/beban 50000 Makanan", desc: "Catat beban + jurnal otomatis" },
            { cmd: "/pendapatan 200000 Jasa", desc: "Catat pendapatan + jurnal" },
            { cmd: "/faktur Budi 500000 Jasa", desc: "🆕 Buat sales invoice (konfirmasi ✅)" },
            { cmd: "📸 Kirim Foto Struk", desc: "🆕 AI Vision → auto expense (konfirmasi ✅)" },
            { cmd: "\"Buatkan penawaran ke PT Budi jasa desain Rp 5jt\"", desc: "🆕 AI → Penawaran / Sales Quotation" },
            { cmd: "\"Bikin SO ke Toko ABC 10 pcs meja @500rb\"", desc: "🆕 AI → Sales Order" },
            { cmd: "\"Order ke Supplier Jaya 20 rim kertas @50rb\"", desc: "🆕 AI → Purchase Order" },
            { cmd: "\"Tagihan CV Alpha kabel Rp 800rb due 14 hari\"", desc: "🆕 AI → Faktur Pembelian" },
            { cmd: "Tanya bebas...", desc: "AI Bookkeeper menjawab pertanyaan Anda" },
          ].map((c, i) => (
            <div key={i} className="bg-slate-800 border border-slate-700 rounded p-3">
              <div className="font-mono-plex text-xs text-amber-400">{c.cmd}</div>
              <div className="text-xs text-slate-300 mt-1">{c.desc}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 text-xs text-slate-400 bg-slate-950 rounded p-3 border border-slate-700">
          <b className="text-amber-400">💡 Tips:</b> Untuk grup Telegram, tambahkan bot ke grup, jadikan admin (agar bisa baca semua pesan). Chat ID grup dimulai dengan <code>-100</code>, tambahkan ke whitelist di atas agar grup bisa memakai bot.
        </div>
      </div>
    </div>
  );
}
